import * as React from 'react';
import { useStore } from 'react-redux';
import { debounce } from '@cian/newbuilding-utils';

import { initNewbuildingSearch } from '../../../../../utils/initer/initer';
import { displayConsultantPhone } from '../../../../../utils/displayNewbuildingConsultantPhone';
import {
  getFirstSalesStartNewbuildingId,
  isSalesStartOnboardingShown,
} from '../../../../../utils/salesStartOnboarding';
import { useApplicationContext } from '../../../../../utils/applicationContext';
import { trackingActionShowSimilarJK } from '../../../../../actions/analytics';
import { setMedia } from '../../../../../actions/media';
import { setSalesStartNewbuildingId, setSalesStartOnboardingStatus } from '../../../../../actions/salesStartOnboarding';
import { DEFAULT_RESIZE_TIMEOUT } from '../../../../../constants/timeouts';
import { APP_HYDRATED_ATTRIBUTE } from '../../../../../constants/hydration';
import { IApplicationState, TReduxStore } from '../../../../../types/redux';
import { TTypedThunkDispatch } from '../../../../../types/thunk';
import { createPopStateHandler, getRealSubdomain, initPushState } from '../../utils/history';

/**
 * Стартовые эффекты приложения (бывший componentDidMount класса Application).
 * Рендерится внутри корневого ErrorLogComponent, поэтому ошибка здесь
 * ловится boundary и не размонтирует весь root.
 */
export const AppLifecycle: React.FC = () => {
  const { config, logger, httpApi } = useApplicationContext();
  const store = useStore<IApplicationState>() as TReduxStore;

  React.useEffect(() => {
    const dispatch = store.dispatch as TTypedThunkDispatch;
    const storeState = store.getState();
    const realSubdomain = getRealSubdomain();

    initNewbuildingSearch({
      logger,
      regionId: storeState.region.currentRegion.id,
      store,
      subdomain: realSubdomain,
      httpApi,
    });

    const similarNewbuildings =
      storeState.offersData.similarNewbuildings && storeState.offersData.similarNewbuildings.length;
    const consultantPhone = config.get<string>('newbuildingConsultant.phone.moscowAndRegion');
    const consultantPhoneEnabledRegions = config.get<number[]>('newbuildingConsultant.phone.enabledRegions') || [];

    displayConsultantPhone(storeState, consultantPhoneEnabledRegions, consultantPhone);
    initPushState(storeState, dispatch, realSubdomain);

    if (similarNewbuildings) {
      dispatch(trackingActionShowSimilarJK(storeState.jsonQuery));
    }

    const handlePopState = createPopStateHandler(dispatch);
    const handleResize = debounce(DEFAULT_RESIZE_TIMEOUT, () => {
      dispatch(setMedia());
    });

    window.addEventListener('popstate', handlePopState, false);
    handleResize();
    window.addEventListener('resize', handleResize, false);

    if (!isSalesStartOnboardingShown()) {
      const firstSalesStartNewbuildingId = getFirstSalesStartNewbuildingId(
        storeState.offersData.newbuildings,
        storeState.offersData.newbuildingPromoSnippet?.newbuilding,
      );

      if (firstSalesStartNewbuildingId) {
        dispatch(setSalesStartNewbuildingId(firstSalesStartNewbuildingId));
        dispatch(setSalesStartOnboardingStatus(true));
      }
    }

    document.documentElement.setAttribute(APP_HYDRATED_ATTRIBUTE, 'true');

    return () => {
      window.removeEventListener('popstate', handlePopState, false);
      window.removeEventListener('resize', handleResize, false);
      handleResize.cancel();
    };
    // Стартовые эффекты выполняются один раз при монтировании
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
